import React from 'react';
import { PaymentAuditEvent } from '@/types/domain/payment-management';
import { formatDate } from '@/lib/utils';
import {
  History,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ShieldCheck,
} from 'lucide-react';

interface PaymentAuditHistoryTabProps {
  auditHistory: PaymentAuditEvent[];
}

export const PaymentAuditHistoryTab: React.FC<PaymentAuditHistoryTabProps> = ({ auditHistory }) => {
  const getEventIcon = (type: PaymentAuditEvent['eventType']) => {
    switch (type) {
      case 'PAYMENT_COMPLETED':
      case 'SAP_SYNC_SUCCESS':
      case 'PAYMENT_APPROVED':
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'PAYMENT_FAILED':
      case 'SAP_SYNC_FAILED':
      case 'PAYMENT_CANCELLED':
        return <AlertTriangle className="h-4 w-4 text-rose-600" />;
      case 'PAYMENT_RETRY_REQUESTED':
      case 'PAYMENT_HOLD_PLACED':
      case 'PAYMENT_HOLD_RELEASED':
        return <Clock className="h-4 w-4 text-amber-600" />;
      default:
        return <History className="h-4 w-4 text-primary" />;
    }
  };

  return (
    <div className="space-y-4 font-heading">
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <div>
          <h3 className="text-sm font-bold text-foreground">Treasury SOX-Compliant Audit Ledger</h3>
          <p className="text-xs text-foreground-muted">
            Immutable chronological record of electronic payment instructions, banking authorizations, and SAP postings.
          </p>
        </div>
        <div className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-500/10 px-2.5 py-1 rounded-full">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>Tamper-Evident Immutable Log</span>
        </div>
      </div>

      {auditHistory.length === 0 ? (
        <div className="p-8 text-center text-foreground-muted text-xs">
          No audit history events recorded.
        </div>
      ) : (
        <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
          {auditHistory.map((ev) => (
            <div key={ev.id} className="relative group">
              <div className="absolute -left-6 top-1 p-1 rounded-full bg-card border border-border shadow-xs">
                {getEventIcon(ev.eventType)}
              </div>

              <div className="p-3.5 rounded-xl border border-border bg-card/60 hover:bg-card transition-colors space-y-1.5 shadow-2xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-foreground">
                      {ev.eventType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-muted text-foreground-muted">
                      {ev.systemSource}
                    </span>
                  </div>
                  <span className="text-[11px] text-foreground-muted font-mono">
                    {formatDate(ev.timestamp)}
                  </span>
                </div>

                <p className="text-xs text-foreground-secondary">{ev.details}</p>

                <div className="flex items-center gap-3 pt-1 text-[11px] text-foreground-muted">
                  <span>
                    Actor: <strong className="text-foreground">{ev.actorName}</strong>
                  </span>
                  <span>•</span>
                  <span>Role: {ev.actorRole}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
